import { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../contexts/AuthContext'
import { useCare } from '../contexts/CareContext'
import LoadingSpinner from '../components/shared/LoadingSpinner'
import InvitePeople from '../components/profile/InvitePeople'
import SpecialistsSection from '../components/profile/SpecialistsSection'

const ROLE_BADGE = {
  admin: { label: 'Admin', style: { background: 'var(--pa-light)', color: 'var(--teal-dark)' } },
  family_member: { label: 'Family member', style: { background: '#EFEFEC', color: '#5A5F5E' } },
  caregiver: { label: 'Caregiver', style: { background: '#EFEFEC', color: '#5A5F5E' } },
  nurse: { label: 'Nurse', style: { background: '#EFE8F7', color: '#6B4A9B' } },
  care_recipient: { label: 'Care recipient', style: { background: '#EFEFEC', color: '#5A5F5E' } },
}

function Avatar({ name, size = 40 }) {
  const initial = (name || '?').trim().charAt(0).toUpperCase()
  return (
    <div
      aria-hidden
      style={{
        width: size, height: size, borderRadius: '50%',
        background: 'var(--pa-light)', color: 'var(--teal-dark)',
        fontWeight: 700, fontSize: size * 0.4, flexShrink: 0,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      {initial}
    </div>
  )
}

export default function TeamPage() {
  const { user } = useAuth()
  const { recipient, role } = useCare()
  const navigate = useNavigate()
  const canManage = role === 'admin' || role === 'family_member'

  const [members, setMembers] = useState([])
  const [loading, setLoading] = useState(true)
  const [inviting, setInviting] = useState(false)

  const load = useCallback(async () => {
    if (!recipient) return
    const { data } = await supabase
      .from('care_team_members')
      .select('id, role, relationship, is_active, user_id, invited_email, profile:profiles(full_name, email)')
      .eq('care_recipient_id', recipient.id)
      .order('role', { ascending: true })
    setMembers(data || [])
    setLoading(false)
  }, [recipient])

  useEffect(() => { load() }, [load])

  if (!recipient || loading) return <LoadingSpinner label="Loading team…" />

  return (
    <div className="stack">
      {/* Team moved out of the bottom nav into More, so it needs its own way back. */}
      <button
        onClick={() => navigate('/more')}
        style={{ background: 'none', border: 'none', color: 'var(--teal)', fontWeight: 600, alignSelf: 'flex-start', padding: 0 }}
      >
        ← More
      </button>

      <h1 className="title">Team</h1>

      {/* Care team */}
      <div className="stack" style={{ gap: 8 }}>
        {members.length === 0 && (
          <div className="card muted" style={{ fontSize: 14 }}>No one on the team yet.</div>
        )}
        {members.map((m) => {
          const isYou = m.user_id && m.user_id === user.id
          const name = m.profile?.full_name || m.invited_email || 'Invited member'
          const badge = ROLE_BADGE[m.role] || ROLE_BADGE.family_member
          const pending = !m.user_id
          return (
            <div key={m.id} className="card">
              <div className="row" style={{ gap: 12 }}>
                <Avatar name={name} />
                <div className="grow" style={{ minWidth: 0 }}>
                  <div
                    style={{
                      fontWeight: 700,
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }}
                  >
                    {name}
                  </div>
                  {m.relationship && (
                    <div className="muted" style={{ fontSize: 13 }}>{m.relationship}</div>
                  )}
                  <div className="row wrap" style={{ gap: 6, marginTop: 6 }}>
                    <span className="badge" style={badge.style}>{badge.label}</span>
                    {isYou && (
                      <span className="badge" style={{ background: 'var(--pa)', color: '#fff' }}>You</span>
                    )}
                    {pending ? (
                      <span className="badge badge-amber">Invited</span>
                    ) : (
                      <span className="badge badge-green">Active</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {/* Doctors and specialists — existing component, already handles its own form */}
      <SpecialistsSection recipientId={recipient.id} canManage={canManage} />

      {/* Invite */}
      {canManage && (
        <div>
          <div className="section-title" style={{ marginBottom: 8 }}>Invite someone</div>
          {inviting ? (
            <div className="card">
              <InvitePeople
                recipient={recipient}
                inviterName={undefined}
                onCancel={() => setInviting(false)}
                onDone={() => { setInviting(false); load() }}
              />
            </div>
          ) : (
            <button className="btn btn-block" onClick={() => setInviting(true)}>
              + Invite a family member or caregiver
            </button>
          )}
        </div>
      )}

      <div style={{ height: 8 }} />
    </div>
  )
}
